import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone } from "lucide-react";

export const AdhesiveCoatingMachine = () => {
  return (
    <>
      <Helmet>
        <title>Adhesive Coating Machine Manufacturer in India - Hero Impex | Hot Melt & Solvent</title>
        <meta name="description" content="Leading adhesive coating machine manufacturer in India. Hot melt, solvent & water-based coating systems. BOPP, PET, paper coating. 45+ years expertise. Get quote!" />
        <meta name="keywords" content="adhesive coating machine manufacturer India, hot melt coating machine, solvent coating equipment, water based coating machine, film coating machine" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-amber-500">Coating Technology Experts Since 1977</Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Adhesive Coating Machine Manufacturer in India
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Advanced adhesive coating solutions for tape, label, and flexible packaging industries. Hot melt, solvent-based, and water-based coating systems with precision control and consistent quality.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8">
                    Get Custom Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+919884012741">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Expert: +91-9884012741
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">India's Premier Adhesive Coating Machine Manufacturer</h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Hero Impex India Private Limited is a trusted name in adhesive coating technology with over 45 years of manufacturing excellence. We design and manufacture high-performance coating machines for hot melt adhesives, solvent-based adhesives, and water-based adhesives.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Our coating machines are used by manufacturers across India and 20+ countries for producing adhesive tapes, labels, protective films, medical tapes, and specialty coated products. We deliver German engineering quality with complete customization support.
              </p>
            </div>

            {/* Coating Technologies */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Coating Technologies We Offer</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-2 border-amber-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                      <span className="text-3xl">🔥</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">Hot Melt Coating</h3>
                    <p className="text-slate-600 mb-4">
                      Ideal for BOPP tapes, packaging tapes, and masking tapes. 100% solid adhesive application with no solvents.
                    </p>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Temperature: 120-180°C</li>
                      <li>• Fast setting time</li>
                      <li>• High bond strength</li>
                      <li>• Eco-friendly process</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2 border-blue-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <span className="text-3xl">💧</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">Solvent-Based Coating</h3>
                    <p className="text-slate-600 mb-4">
                      Perfect for specialty tapes, double-sided tapes, and high-performance adhesive applications.
                    </p>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Superior adhesion</li>
                      <li>• Thin coat weight</li>
                      <li>• Wide viscosity range</li>
                      <li>• Precision coating</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2 border-green-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <span className="text-3xl">🌊</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">Water-Based Coating</h3>
                    <p className="text-slate-600 mb-4">
                      Environmentally friendly coating for labels, stickers, and eco-conscious manufacturers.
                    </p>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Zero VOC emissions</li>
                      <li>• Non-flammable</li>
                      <li>• Cost-effective</li>
                      <li>• Safe operation</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Why Choose Hero Impex */}
            <div className="mb-12 bg-amber-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose Hero Impex Coating Machines?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Precision Coating Control</h4>
                    <p className="text-slate-600">Accurate coat weight control with comma bar, gravure, or reverse roll coating methods</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Multi-Layer Coating Capability</h4>
                    <p className="text-slate-600">Option for simultaneous primer and adhesive coating</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Efficient Drying Systems</h4>
                    <p className="text-slate-600">Hot air circulation, IR, or UV drying as per adhesive type</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Automatic Tension Control</h4>
                    <p className="text-slate-600">Prevents film stretching and coating defects</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">PLC & HMI Control</h4>
                    <p className="text-slate-600">Easy recipe management and process monitoring</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Complete Turnkey Solutions</h4>
                    <p className="text-slate-600">From raw material handling to finished roll packaging</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Technical Specifications</h2>
              <Card className="border-2 border-amber-200">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-slate-900 mb-3">Machine Parameters</h4>
                      <ul className="space-y-2">
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Coating Width:</span>
                          <span className="font-semibold text-slate-900">500mm - 1600mm</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Machine Speed:</span>
                          <span className="font-semibold text-slate-900">Up to 200 m/min</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Coat Weight Range:</span>
                          <span className="font-semibold text-slate-900">5-50 GSM</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Unwind Diameter:</span>
                          <span className="font-semibold text-slate-900">Max 1000mm</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Rewind Diameter:</span>
                          <span className="font-semibold text-slate-900">Max 800mm</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-3">Standard Features</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Automatic coating applicator system</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Temperature control for adhesive tank</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Multi-zone drying oven</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Edge Position Control (EPC)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Corona treatment system (optional)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Online slitting attachment</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Applications */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Products You Can Manufacture</h2>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  'BOPP Packing Tape',
                  'Masking Tape',
                  'Double-Sided Tape',
                  'Foam Tape',
                  'Label Stock',
                  'Protective Films',
                  'Medical Tapes',
                  'Transfer Tape',
                  'Specialty Adhesive Tapes',
                  'Sticker Stock',
                  'Surface Protection Tape',
                  'Automotive Tapes'
                ].map((product) => (
                  <Card key={product} className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                    <CardContent className="p-4 text-center">
                      <span className="font-semibold text-slate-900 text-sm">{product}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-10 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">Start Your Adhesive Coating Business Today</h2>
              <p className="text-xl mb-6 text-amber-50">
                Get expert consultation on the right coating technology for your product. Our technical team will help you choose between hot melt, solvent, or water-based systems.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 text-lg px-8">
                    Request Detailed Quote
                  </Button>
                </Link>
                <a href="https://wa.me/919884012741?text=I need information about adhesive coating machines" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                    WhatsApp: +91-9884012741
                  </Button>
                </a>
              </div>
              <p className="mt-6 text-sm text-amber-100">
                ✓ Coating Technology Experts • ✓ Custom Solutions • ✓ Installation & Training • ✓ 45+ Years Trust
              </p>
            </div>

          </div>
        </section>
      </div>
    </>
  );
};